export interface SonioxTemporaryConfiguration {
  apiKey: string;
  websocketUri: string;
  expiresAtMs: number;
}

const REFRESH_BUFFER_MS = 2 * 60 * 1000; // 2 minutes

let cachedConfiguration: SonioxTemporaryConfiguration | null = null;
let inFlightConfiguration: Promise<SonioxTemporaryConfiguration> | null = null;

/**
 * Returns true when a temporary key is still safe to use for a new session.
 */
const isFresh = (configuration: SonioxTemporaryConfiguration): boolean => {
  return configuration.expiresAtMs - Date.now() > REFRESH_BUFFER_MS;
};

/**
 * Fetches a temporary Soniox configuration with in-memory caching and single-flight deduplication.
 * Use `forceRefresh` for auth-retry paths where a stale token is suspected.
 */
export const getTemporaryConfiguration = async (
  fetchConfiguration: () => Promise<SonioxTemporaryConfiguration>,
  options?: { forceRefresh?: boolean },
): Promise<SonioxTemporaryConfiguration> => {
  const forceRefresh = options?.forceRefresh === true;

  if (
    !forceRefresh &&
    cachedConfiguration != null &&
    isFresh(cachedConfiguration)
  ) {
    return cachedConfiguration;
  }

  if (inFlightConfiguration != null && !forceRefresh) {
    return inFlightConfiguration;
  }

  inFlightConfiguration = (async () => {
    const next = await fetchConfiguration();
    cachedConfiguration = next;
    return next;
  })().finally(() => {
    inFlightConfiguration = null;
  });

  return inFlightConfiguration;
};

/**
 * Warms the temporary Soniox configuration cache.
 * Failure is non-fatal because start flow will fetch again on demand.
 */
export const warmTemporaryConfiguration = async (
  fetchConfiguration: () => Promise<SonioxTemporaryConfiguration>,
): Promise<void> => {
  try {
    await getTemporaryConfiguration(fetchConfiguration);
  } catch (error) {
    console.warn('Failed to prefetch Soniox temporary API key', error);
  }
};

/**
 * Clears cached temporary Soniox configuration.
 */
export const invalidateTemporaryConfiguration = (): void => {
  cachedConfiguration = null;
};
